import "./BcEhtDataGridStyle.css";

import {
	Box,
	CircularProgress,
	Grid,
	Typography,
	useTheme,
} from "@mui/material";
import {
	EnhancedButtonPrimeReact,
	EnhancedButtonPrimeReactSeverity,
	EnhancedDropDownRHF,
	EnhancedDropDownRHFWithLabel,
	LabelPosition,
	RHookFormNumberField,
	RequestParams,
} from "@websential/cosmic-v20";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	formatAmountToFloat,
	formatDate,
	formatDateToShow,
	formatThousandSeperatorWithTwoFixedAmount,
} from "../../../common/Common";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import isEqual from "lodash/isEqual";

export interface IBcEhtDataGrid {
	rows: any;
	columns: any;
	onRowDeleteClicked?: (params: any) => void;
	onRowRemitClicked?: (params: any) => void;
	tableScrollHeight?: string;
	onSubmitButtonClicked: () => void;
	ehtAmount: string | number;
	installmentAmount: number | string;
	previousYearEmloyerEht: number;
	bcEhtDataGridItems: (data: any) => void;
	bcEhtData: any;
}

export const BcEhtDataGrid: React.FC<IBcEhtDataGrid> = ({
	rows,
	columns,
	onRowDeleteClicked,
	onRowRemitClicked,
	tableScrollHeight,
	onSubmitButtonClicked,
	ehtAmount,
	installmentAmount,
	previousYearEmloyerEht,
	bcEhtDataGridItems,
	bcEhtData,
}) => {
	const isInitialRequest = useRef(true);
	const [loading, setLoading] = useState(false);
	const [bcEhtItems, setBcEhtItems] = useState<any>(rows && rows);

	useEffect(() => {
		bcEhtDataGridItems(bcEhtItems);
		
	}, [bcEhtItems]);

	useEffect(() => {
		if (!isEqual(rows, bcEhtItems)) {
			setBcEhtItems([...rows]);
		}
	}, [rows]);

	const theme = useTheme();
	const onCellValueChange = (event: any, rowIndex: number, field: any) => {
		setBcEhtItems((prev: any) => {
			const updatedData = [...prev];
			const updatedRow = { ...updatedData[rowIndex], [event.field]: field };
			updatedData[rowIndex] = updatedRow;
			return updatedData;
		});
	};

	const columnsBody = (rowData: any, row: any, x: any) => {
		if (x.field === "remitButton") {
			if (rowData.canDelete) {
				return (
					<div style={{ display: "flex", alignItems: "center" }}>
						<EnhancedButtonPrimeReact
							label="Remit"
							severity={EnhancedButtonPrimeReactSeverity.Success}
							raised
							disabled={true}
						/>
						<EnhancedButtonPrimeReact
							// icon="pi pi-trash"
							icon="pi pi-times"
							onClickHandler={() =>
								onRowDeleteClicked && onRowDeleteClicked(bcEhtItems)
							}
							severity={EnhancedButtonPrimeReactSeverity.Danger}
							className="p-button-rounded p-button-text w-2rem h-1rem deleteButton"
							rounded
						/>
					</div>
				);
			} else {
				return (
					<EnhancedButtonPrimeReact
						label="Remit"
						onClickHandler={() =>
							onRowRemitClicked && onRowRemitClicked(bcEhtItems)
						}
						severity={EnhancedButtonPrimeReactSeverity.Success}
						raised
						disabled={rowData.canEdit ? false : true}
					/>
				);
			}
		}
		if (x.field === "chequeDate") {
			if (rowData.canEdit) {
				return (
					<Calendar
						value={rowData[x.field]}
						// className="calender"
						style={{
							margin: "8px 0px 4px 0px",
							width: "auto",
						}}
						onChange={(e: any) => onCellValueChange(x, row.rowIndex, e.value)}
						dateFormat="M dd, yy"
						placeholder={"dd/mm/yyyy"}
						showButtonBar
						// className={renderErrorClass(x, row.rowIndex,rowData)}
					/>
				);
			} else {
				return formatDateToShow(rowData[x.field]);
			}
		}
		if (x.field === "remitAmount") {
			if (rowData.canEdit) {
				return (
					<InputNumber
						value={rowData.remitAmount}
						onChange={(e: any) => onCellValueChange(x, row.rowIndex, e.value)}
						placeholder="0.00"
						minFractionDigits={2}
						maxFractionDigits={2}
						inputStyle={{ textAlign: "right" }}
					/>
				);
			} else {
				return (
					<span>
						{rowData[x.field] !== null
							? rowData[x.field]
									.toFixed(2)
									.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
							: rowData[x.field]}
					</span>
				);
			}
		}
		if (x.field === "chequeNo") {
			if (rowData.canEdit) {
				return (
					<InputText
						value={rowData[x.field]}
						onChange={(e: any) =>
							onCellValueChange(x, row.rowIndex, e.target.value)
						}
						className={"chequeNoInputField"}
						// style={{ maxWidth: "82px!important",minWidth: "82px!important" }}
					/>
				);
			} else {
				return <span>{rowData[x.field]}</span>;
			}
		}

		if (x.field === "dueDate" || x.field === "dueAmount") {
			return <span>{rowData[x.field]}</span>;
		}
	};

	const renderHeader = () => {
		return (
			<Box
				sx={{
					textAlign: "right",
					color: "rgba(0, 0, 0, 0.6)",
					fontSize: "0.875rem",
				}}
			>
				<Typography
					sx={{
						fontSize: "0.875rem",
						textAlign: "left",
						paddingBottom: "10px",
					}}
				>
					Your estimated Employer Health Tax amount = ${ehtAmount} (Due date is
					March 31 2026)
				</Typography>
				<Typography
					sx={{
						fontSize: "0.875rem",
						textAlign: "left",
						// paddingBottom: "10px",
					}}
				>
					{ehtAmount && formatAmountToFloat(ehtAmount.toString()) !== 0 ? (
						<>
							Since your previous year's Employer Health Tax is over $
							{installmentAmount}, you are required to make quarterly
							instalment payments.
						</>
					) : (
						<></>
					)}
				</Typography>
					{/* umair  */}
				{ehtAmount && formatAmountToFloat(ehtAmount.toString()) !== 0 && bcEhtData && previousYearEmloyerEht < formatAmountToFloat(installmentAmount.toString()) ? (
					<Grid
						size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
						sx={{ display: "flex", mt: "1.5rem" }}
					>
						<Grid
							size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
							sx={{ textAlign: "left" }}
						>
							<EnhancedDropDownRHFWithLabel
								label="Please choose an option:"
								selectName="chooseAnOption"
								testId={`chooseAnOption`}
								items={[
									{ label: "Make one payment", value: false },
									{ label: "Pay in installments", value: true },
								]}
								selectedItem={false}
								labelColumns={5}
								fieldColumns={7}
								labelPosition={LabelPosition.Left}
							/>
						</Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 1.6, lg: 1.6, xl: 1.6 }}
							sx={{ textAlign: "center" }}
						>
							<EnhancedButtonPrimeReact
								label="Submit"
								severity={EnhancedButtonPrimeReactSeverity.Success}
								raised
								onClickHandler={onSubmitButtonClicked}
								style={{ marginTop: "1px" }}
								disabled={bcEhtData.remitted ? true : false}
							/>
						</Grid>
					</Grid>
				) : (
					<></>
				)}
			</Box>
		);
	};
	const headerSearch = renderHeader();



	return (
		<>
			<DataTable
				value={rows && columns && bcEhtItems}
				responsiveLayout="scroll"
				showGridlines
				resizableColumns
				columnResizeMode="expand"
				scrollable
				scrollHeight={tableScrollHeight ? "75vh" : ""}
				emptyMessage={
					rows && rows !== null && rows.length > 0 ? "No Record found." : " "
				}
				className="BcEhtDataGrid"
				sortMode="single"
				header={headerSearch}
				style={{ marginTop: "18px" }}
			>
				{rows?.length > 0 &&
					columns
						?.sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0))
						.map((x: any) => (
							<Column
								key={x.field}
								field={x.field}
								header={x.headerName}
								hidden={x.hide}
								reorderable={false}
								dataType={x.type}
								align={x.align}
								alignHeader={x.headerAlign}
								exportable={!x.disableExport}
								frozen={x.frozenColumn}
								alignFrozen={x.alignFrozen}
								style={{
									minWidth: x.minWidth,
									maxWidth: x.maxWidth,
									fontSize: "0.875rem",
								}}
								body={(rowData, row) => columnsBody(rowData, row, x)}
							/>
						))}
			</DataTable>
		</>
	);
};
